import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getOrder, getCustomer } from '../../../app/api';
import { useWorkspace } from '../../../app/WorkspaceContext';
import { formatCurrency } from '../../../app/currency';
import { buildQuoteModel } from '../private/quoteExport';

/** Client-facing printable quote: /orders/:id/quote (opened in a new tab from the order page). */
const PRINT_CSS = `
  @page { size: A4; margin: 16mm; }
  .eq-root { min-height: 100vh; background: #f3f4f6; color: #111827; font-family: 'Inter', 'Helvetica Neue', Arial, sans-serif; padding: 32px 16px; box-sizing: border-box; }
  .eq-toolbar { max-width: 794px; margin: 0 auto 16px; display: flex; justify-content: flex-end; gap: 8px; }
  .eq-toolbar button { font: inherit; font-size: 14px; padding: 8px 18px; border-radius: 8px; border: 1px solid #d1d5db; background: #fff; color: #111827; cursor: pointer; }
  .eq-toolbar button.eq-primary { background: #111827; border-color: #111827; color: #fff; }
  .eq-sheet { max-width: 794px; margin: 0 auto; background: #fff; padding: 56px 56px 48px; box-sizing: border-box; box-shadow: 0 1px 3px rgba(0,0,0,.08); border-radius: 4px; }
  .eq-head { display: flex; justify-content: space-between; align-items: flex-start; gap: 24px; padding-bottom: 28px; border-bottom: 2px solid #111827; }
  .eq-company { font-size: 22px; font-weight: 700; letter-spacing: -0.01em; }
  .eq-doc { text-align: right; }
  .eq-doc-label { font-size: 11px; letter-spacing: .14em; text-transform: uppercase; color: #6b7280; }
  .eq-doc-number { font-size: 22px; font-weight: 700; font-variant-numeric: tabular-nums; }
  .eq-meta { display: grid; grid-template-columns: 1.4fr 1fr 1fr 1fr; gap: 16px; padding: 24px 0; border-bottom: 1px solid #e5e7eb; }
  .eq-k { font-size: 11px; letter-spacing: .08em; text-transform: uppercase; color: #6b7280; margin-bottom: 4px; }
  .eq-v { font-size: 14px; line-height: 1.45; }
  .eq-v small { display: block; color: #6b7280; font-size: 12px; }
  .eq-title { font-size: 18px; font-weight: 600; margin: 28px 0 12px; }
  table.eq-items { width: 100%; border-collapse: collapse; font-size: 14px; }
  .eq-items th { text-align: left; font-size: 11px; letter-spacing: .08em; text-transform: uppercase; color: #6b7280; font-weight: 600; padding: 10px 8px; border-bottom: 1px solid #e5e7eb; }
  .eq-items td { padding: 14px 8px; border-bottom: 1px solid #f0f1f3; vertical-align: top; }
  .eq-items .num { text-align: right; white-space: nowrap; font-variant-numeric: tabular-nums; }
  .eq-item-name { font-weight: 600; }
  .eq-item-sub { color: #6b7280; font-size: 12.5px; margin-top: 3px; white-space: pre-line; }
  .eq-totals { margin-left: auto; width: 300px; margin-top: 16px; font-size: 14px; }
  .eq-totals div { display: flex; justify-content: space-between; padding: 6px 8px; font-variant-numeric: tabular-nums; }
  .eq-totals .eq-grand { border-top: 2px solid #111827; margin-top: 6px; padding-top: 12px; font-size: 18px; font-weight: 700; }
  .eq-terms { display: grid; grid-template-columns: 1fr 1fr; gap: 24px; margin-top: 36px; padding-top: 24px; border-top: 1px solid #e5e7eb; }
  .eq-notes { margin-top: 24px; font-size: 13.5px; line-height: 1.6; white-space: pre-line; color: #374151; }
  .eq-foot { margin-top: 48px; font-size: 12px; color: #9ca3af; text-align: center; }
  .eq-msg { max-width: 560px; margin: 80px auto; text-align: center; font-size: 15px; color: #374151; }
  @media (max-width: 640px) {
    .eq-sheet { padding: 28px 20px; }
    .eq-meta, .eq-terms { grid-template-columns: 1fr 1fr; }
    .eq-totals { width: 100%; }
  }
  @media print {
    .eq-root { background: #fff; padding: 0; }
    .eq-toolbar { display: none; }
    .eq-sheet { box-shadow: none; padding: 0; max-width: none; border-radius: 0; }
    tr { break-inside: avoid; }
  }
`;

const OrderQuotePrintPage = () => {
  const { id } = useParams();
  const { currentWorkspace } = useWorkspace();
  const [model, setModel] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await getOrder(id);
        const order = res?.data;
        let customer = null;
        if (order?.customer_id) {
          try {
            customer = (await getCustomer(order.customer_id))?.data || null;
          } catch {
            customer = null;
          }
        }
        if (!cancelled) setModel(buildQuoteModel(order, customer));
      } catch (e) {
        if (!cancelled) setError(e.message || 'Não foi possível carregar o orçamento.');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  const companyName = currentWorkspace?.name || '';

  useEffect(() => {
    if (model) document.title = `Orçamento ${model.number}${model.customerName ? ` - ${model.customerName}` : ''}`;
  }, [model]);

  return (
    <div className="eq-root">
      <style>{PRINT_CSS}</style>
      {error ? <div className="eq-msg">{error}</div> : null}
      {!error && !model ? <div className="eq-msg">Carregando orçamento…</div> : null}
      {model ? (
        <>
          <div className="eq-toolbar">
            <button type="button" onClick={() => window.close()}>
              Fechar
            </button>
            <button type="button" className="eq-primary" onClick={() => window.print()}>
              Imprimir / salvar PDF
            </button>
          </div>
          <article className="eq-sheet">
            <header className="eq-head">
              <div className="eq-company">{companyName}</div>
              <div className="eq-doc">
                <div className="eq-doc-label">Orçamento</div>
                <div className="eq-doc-number">{model.number}</div>
              </div>
            </header>

            <section className="eq-meta">
              <div>
                <div className="eq-k">Cliente</div>
                <div className="eq-v">
                  {model.customerName}
                  {model.customerPhone ? <small>{model.customerPhone}</small> : null}
                  {model.customerEmail ? <small>{model.customerEmail}</small> : null}
                </div>
              </div>
              <div>
                <div className="eq-k">Emissão</div>
                <div className="eq-v">{model.issuedAt}</div>
              </div>
              <div>
                <div className="eq-k">Válido até</div>
                <div className="eq-v">{model.validUntil || '—'}</div>
              </div>
              <div>
                <div className="eq-k">Entrega prevista</div>
                <div className="eq-v">{model.deliveryDate || '—'}</div>
              </div>
            </section>

            {model.title ? <h1 className="eq-title">{model.title}</h1> : null}

            <table className="eq-items">
              <thead>
                <tr>
                  <th>Item</th>
                  <th className="num">Qtd.</th>
                  <th className="num">Valor unit.</th>
                  <th className="num">Total</th>
                </tr>
              </thead>
              <tbody>
                {model.items.map((it) => (
                  <tr key={it.id}>
                    <td>
                      <div className="eq-item-name">{it.name}</div>
                      {it.attributes ? <div className="eq-item-sub">{it.attributes}</div> : null}
                      {it.details ? <div className="eq-item-sub">{it.details}</div> : null}
                    </td>
                    <td className="num">{it.quantity}</td>
                    <td className="num">{formatCurrency(it.unitPrice)}</td>
                    <td className="num">{formatCurrency(it.total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="eq-totals">
              {model.discount > 0 ? (
                <>
                  <div>
                    <span>Subtotal</span>
                    <span>{formatCurrency(model.subtotal)}</span>
                  </div>
                  <div>
                    <span>Desconto</span>
                    <span>− {formatCurrency(model.discount)}</span>
                  </div>
                </>
              ) : null}
              <div className="eq-grand">
                <span>Total</span>
                <span>{formatCurrency(model.total)}</span>
              </div>
            </div>

            <section className="eq-terms">
              <div>
                <div className="eq-k">Condições de pagamento</div>
                <div className="eq-v">{model.paymentTerms || '—'}</div>
              </div>
              <div>
                <div className="eq-k">Prazo de entrega</div>
                <div className="eq-v">{model.deliveryDate ? `Previsão para ${model.deliveryDate}` : '—'}</div>
              </div>
            </section>

            {model.customerNotes ? <div className="eq-notes">{model.customerNotes}</div> : null}

            <footer className="eq-foot">
              {model.validUntil ? `Orçamento válido até ${model.validUntil}. ` : ''}
              {companyName ? `Obrigado pela preferência — ${companyName}.` : 'Obrigado pela preferência.'}
            </footer>
          </article>
        </>
      ) : null}
    </div>
  );
};

export default OrderQuotePrintPage;
